import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { format, parseISO } from "date-fns";

interface ProductCMV {
  productId: string;
  productName: string;
  quantitySold: number;
  totalRevenue: number;
  totalCost: number;
  margin: number;
  marginPercentage: number;
}

interface IngredientConsumption {
  ingredientId: string;
  ingredientName: string;
  unit: string;
  quantityConsumed: number;
  averageCost: number;
  totalCost: number;
}

interface DailyCMV {
  date: string;
  totalRevenue: number;
  totalCost: number;
  margin: number;
  marginPercentage: number;
}

export interface CMVReportData {
  totalRevenue: number;
  totalCost: number;
  totalMargin: number;
  marginPercentage: number;
  productsCMV: ProductCMV[];
  ingredientConsumption: IngredientConsumption[];
  dailyCMV: DailyCMV[];
}

interface UseCMVReportParams {
  dateFrom: Date;
  dateTo: Date;
}

export function useCMVReport({ dateFrom, dateTo }: UseCMVReportParams) {
  const { activeRestaurant } = useRestaurantContext();

  return useQuery({
    queryKey: ["cmv-report", activeRestaurant?.id, dateFrom, dateTo],
    queryFn: async (): Promise<CMVReportData> => {
      if (!activeRestaurant) {
        return {
          totalRevenue: 0,
          totalCost: 0,
          totalMargin: 0,
          marginPercentage: 0,
          productsCMV: [],
          ingredientConsumption: [],
          dailyCMV: [],
        };
      }

      const startDate = format(dateFrom, "yyyy-MM-dd");
      const endDate = format(dateTo, "yyyy-MM-dd");

      // Fetch stock movements of type 'sale' within the date range
      const { data: saleMovements, error: movementsError } = await supabase
        .from("stock_movements" as any)
        .select(`
          *,
          ingredients(id, name, unit, cost_per_unit)
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .eq("type", "sale")
        .gte("created_at", `${startDate}T00:00:00`)
        .lte("created_at", `${endDate}T23:59:59`)
        .order("created_at", { ascending: true });

      if (movementsError) {
        console.error("Error fetching sale movements:", movementsError);
        throw movementsError;
      }

      // Fetch orders within the date range for revenue calculation
      const { data: orders, error: ordersError } = await supabase
        .from("orders")
        .select(`
          id,
          total,
          subtotal,
          created_at,
          order_items(
            product_id,
            product_name,
            quantity,
            total_price
          )
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .neq("status", "cancelled")
        .gte("created_at", `${startDate}T00:00:00`)
        .lte("created_at", `${endDate}T23:59:59`);

      if (ordersError) {
        console.error("Error fetching orders:", ordersError);
        throw ordersError;
      }

      // Fetch product_ingredients to map products to their ingredients
      const productIds = [...new Set((orders || []).flatMap(o => 
        (o.order_items || []).map((item: any) => item.product_id).filter(Boolean)
      ))];

      // Fetch products data (cost_per_unit and tracks_stock) for resale products
      const { data: productsData } = await supabase
        .from("products")
        .select("id, name, cost_per_unit, tracks_stock")
        .eq("restaurant_id", activeRestaurant.id)
        .in("id", productIds.length > 0 ? productIds : ["00000000-0000-0000-0000-000000000000"]);

      const productsMap = new Map(
        (productsData || []).map(p => [p.id, p])
      );

      const { data: productIngredients, error: piError } = await supabase
        .from("product_ingredients" as any)
        .select(`
          product_id,
          ingredient_id,
          quantity,
          ingredients(id, name, cost_per_unit)
        `)
        .in("product_id", productIds.length > 0 ? productIds : ["00000000-0000-0000-0000-000000000000"]);

      if (piError) {
        console.error("Error fetching product ingredients:", piError);
      }

      // Calculate ingredient consumption totals
      const ingredientConsumptionMap = new Map<string, IngredientConsumption>();
      
      for (const movement of (saleMovements || []) as any[]) {
        const ingredient = movement.ingredients;
        if (!ingredient) continue;

        const existing = ingredientConsumptionMap.get(ingredient.id);
        const quantityConsumed = Math.abs(movement.quantity);
        const unitCost = movement.unit_cost > 0 ? movement.unit_cost : ingredient.cost_per_unit;
        const totalCost = quantityConsumed * unitCost;

        if (existing) {
          existing.quantityConsumed += quantityConsumed;
          existing.totalCost += totalCost;
          existing.averageCost = existing.totalCost / existing.quantityConsumed;
        } else {
          ingredientConsumptionMap.set(ingredient.id, {
            ingredientId: ingredient.id,
            ingredientName: ingredient.name,
            unit: ingredient.unit,
            quantityConsumed,
            averageCost: unitCost,
            totalCost,
          });
        }
      }

      // Calculate product CMV
      const productCMVMap = new Map<string, ProductCMV>();
      const piMap = new Map<string, any[]>();
      
      for (const pi of (productIngredients || []) as any[]) {
        const existing = piMap.get(pi.product_id) || [];
        existing.push(pi);
        piMap.set(pi.product_id, existing);
      }

      for (const order of orders || []) {
        for (const item of (order.order_items || []) as any[]) {
          if (!item.product_id) continue;

          const existing = productCMVMap.get(item.product_id);
          const revenue = item.total_price;
          
          // Calculate cost from ingredients or product cost_per_unit
          const ingredients = piMap.get(item.product_id) || [];
          const product = productsMap.get(item.product_id);
          let itemCost = 0;
          
          if (ingredients.length > 0) {
            // Product with recipe: calculate cost from ingredients
            for (const pi of ingredients) {
              const ingredientCost = pi.ingredients?.cost_per_unit || 0;
              itemCost += pi.quantity * ingredientCost * item.quantity;
            }
          } else if (product?.tracks_stock && (product.cost_per_unit || 0) > 0) {
            // Resale product: use direct product cost
            itemCost = (product.cost_per_unit || 0) * item.quantity;
          }

          if (existing) {
            existing.quantitySold += item.quantity;
            existing.totalRevenue += revenue;
            existing.totalCost += itemCost;
            existing.margin = existing.totalRevenue - existing.totalCost;
            existing.marginPercentage = existing.totalRevenue > 0 
              ? (existing.margin / existing.totalRevenue) * 100 
              : 0;
          } else {
            const margin = revenue - itemCost;
            productCMVMap.set(item.product_id, {
              productId: item.product_id,
              productName: item.product_name,
              quantitySold: item.quantity,
              totalRevenue: revenue,
              totalCost: itemCost,
              margin,
              marginPercentage: revenue > 0 ? (margin / revenue) * 100 : 0,
            });
          }
        }
      }

      // Calculate daily CMV
      const dailyCMVMap = new Map<string, DailyCMV>();
      
      for (const order of orders || []) {
        const date = format(parseISO(order.created_at), "yyyy-MM-dd");
        const existing = dailyCMVMap.get(date);
        const revenue = order.subtotal;
        
        // Calculate cost for this order
        let orderCost = 0;
        for (const item of (order.order_items || []) as any[]) {
          if (!item.product_id) continue;
          const ingredients = piMap.get(item.product_id) || [];
          const product = productsMap.get(item.product_id);
          
          if (ingredients.length > 0) {
            for (const pi of ingredients) {
              const ingredientCost = pi.ingredients?.cost_per_unit || 0;
              orderCost += pi.quantity * ingredientCost * item.quantity;
            }
          } else if (product?.tracks_stock && (product.cost_per_unit || 0) > 0) {
            orderCost += (product.cost_per_unit || 0) * item.quantity;
          }
        }

        if (existing) {
          existing.totalRevenue += revenue;
          existing.totalCost += orderCost;
          existing.margin = existing.totalRevenue - existing.totalCost;
          existing.marginPercentage = existing.totalRevenue > 0 
            ? (existing.margin / existing.totalRevenue) * 100 
            : 0;
        } else {
          const margin = revenue - orderCost;
          dailyCMVMap.set(date, {
            date,
            totalRevenue: revenue,
            totalCost: orderCost,
            margin,
            marginPercentage: revenue > 0 ? (margin / revenue) * 100 : 0,
          });
        }
      }

      // Calculate totals
      const totalRevenue = Array.from(productCMVMap.values()).reduce((sum, p) => sum + p.totalRevenue, 0);
      const totalCost = Array.from(productCMVMap.values()).reduce((sum, p) => sum + p.totalCost, 0);
      const totalMargin = totalRevenue - totalCost;
      const marginPercentage = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

      return {
        totalRevenue,
        totalCost,
        totalMargin,
        marginPercentage,
        productsCMV: Array.from(productCMVMap.values()).sort((a, b) => b.totalRevenue - a.totalRevenue),
        ingredientConsumption: Array.from(ingredientConsumptionMap.values()).sort((a, b) => b.totalCost - a.totalCost),
        dailyCMV: Array.from(dailyCMVMap.values()).sort((a, b) => a.date.localeCompare(b.date)),
      };
    },
    enabled: !!activeRestaurant,
  });
}
